"use client";

import { usePathname } from "next/navigation";
import React, { useEffect, useState } from "react";

import { Ad } from "@/components/Advert/Ad";
import {
  AllowedPages,
  PageTypeMap,
} from "@/components/Advert/enums/allowed-pages.enum";
import { FooterAd } from "@/components/Advert/FooterAd";
import { TopPageAd } from "@/components/Advert/TopPageAd";
import {
  AdvertisingGetAdPageType,
  AdvertisingGetAdPlaceholderType,
  useAdvertisingGetAd,
  useAdvertisingTrackAdClick,
} from "@/hooks/api/generated";
import useDeviceType from "@/hooks/useDeviceType";
import useGetUserCountry from "@/hooks/useGetUserCountry";

interface IAdvertProps {
  type?: AdvertisingGetAdPlaceholderType;
  className?: string;
}

export function Advert({
  type = AdvertisingGetAdPlaceholderType.top_of_page,
  className,
}: IAdvertProps): JSX.Element | null {
  const { country } = useGetUserCountry();
  const deviceType = useDeviceType();
  const pathname = usePathname();
  const [isAdViewable, setIsAdViewable] = useState<boolean>(false);
  const [pageType, setPageType] = useState<AdvertisingGetAdPageType | null>(
    null
  );
  const { mutate: advertTrackClick } = useAdvertisingTrackAdClick();
  useEffect(() => {
    if (!pathname) {
      return;
    }

    const matchedPage = Object.entries(AllowedPages).find(
      ([_key, path]) => pathname === path || pathname.startsWith(`${path}/`)
    );

    if (matchedPage) {
      const [_enumKey, basePath] = matchedPage;

      const isDetailPage = pathname.startsWith(`${basePath}/`);

      const newPageType = isDetailPage
        ? PageTypeMap[`${basePath}/` as keyof typeof PageTypeMap]
        : PageTypeMap[basePath as keyof typeof PageTypeMap];

      setIsAdViewable(true);
      setPageType(newPageType);
    } else {
      setIsAdViewable(false);
      setPageType(null);
    }
  }, [pathname]);
  const { data } = useAdvertisingGetAd(
    {
      pageType: pageType ?? AdvertisingGetAdPageType.job_listing,
      placeholderType: type,
      userCountry: country || "",
      isMobileWeb: deviceType === "mobile" ? "true" : "false",
    },
    {
      query: {
        queryKey: ["advert", pageType, type, country, deviceType],
        retry: true,
        enabled: !!country,
        refetchOnWindowFocus: false,
      },
    }
  );

  if (!isAdViewable || !pageType || !data?.hasAd) {
    return null;
  }

  const handleAdvertTrackClick = (campaignPlaceholderId: number): void => {
    advertTrackClick({ campaignPlaceholderId });
  };

  let adComponent;
  switch (type) {
    case AdvertisingGetAdPlaceholderType.top_of_page:
      adComponent = (
        <TopPageAd advert={data} handleTrackClick={handleAdvertTrackClick} />
      );
      break;
    case AdvertisingGetAdPlaceholderType.bottom_of_page:
      adComponent = (
        <FooterAd advert={data} handleTrackClick={handleAdvertTrackClick} />
      );
      break;
    default:
      adComponent = (
        <Ad advert={data} handleTrackClick={handleAdvertTrackClick} />
      );
  }

  return <div className={className}>{adComponent}</div>;
}
